import BYOBStateManager from "./byob-state-manager.js";
import BYOBDiscountCalculator from "./byob-discount-calculator.js";
import BYOBCartSubmitter from "./byob-cart-submitter.js";
import "./byob-product-card.js";
import "./byob-variant-drawer.js";

/**
 * @typedef {Object} SkioProduct
 * @property {string} platformId - Shopify GID for the product
 * @property {string} title - Product title
 * @property {string} handle - Product handle
 * @property {string} [description] - Product description
 * @property {Object} featuredImage - Featured image object
 * @property {string} featuredImage.url - Image URL
 * @property {string} featuredImage.altText - Image alt text
 * @property {Array<SkioVariant>} variants - Product variants
 */

/**
 * @typedef {Object} SkioVariant
 * @property {string} platformId - Shopify GID for the variant
 * @property {string} title - Variant title
 * @property {Object} price - Price object
 * @property {string} price.amount - Price amount
 * @property {string} price.currencyCode - Currency code
 * @property {Object|null} compareAtPrice - Compare at price object
 * @property {string} [compareAtPrice.amount] - Compare at price amount
 * @property {Object} [image] - Variant image
 */

/**
 * @typedef {Object} SkioDiscountTier
 * @property {number} quantity - Quantity threshold
 * @property {number} discount - Discount percentage or fixed amount
 */

/**
 * @typedef {Object} SkioSellingPlanGroup
 * @property {string} platformId - Selling plan group GID
 * @property {string} name - Selling plan group name
 * @property {Array<Object>} sellingPlans - Available selling plans
 */

/**
 * @typedef {Object} GroupedProducts
 * @property {string} category - Category name
 * @property {Array<SkioProduct>} products - Products in this category
 */

/**
 * @typedef {Object} BundleConfig
 * @property {string} boxProductId - Box product GID
 * @property {string} boxId - Skio box ID
 * @property {string} boxVariantId - Parent variant GID
 * @property {number} minItems - Minimum bundle size
 * @property {number} maxItems - Maximum bundle size
 * @property {number} freeShippingThreshold - Free shipping threshold in cents
 * @property {string} storefrontToken - Shopify Storefront API token
 * @property {string} boxSellingPlanId - Skio selling plan ID for the box product (store-specific)
 * @property {string} mainSellingPlanId - Skio selling plan ID for main bundle products (store-specific)
 */

/**
 * Main orchestrator web component for BYOB (Build Your Own Bundle).
 * Coordinates product fetching, rendering, state management, and cart submission.
 * @extends HTMLElement
 */
class BundleProductList extends HTMLElement {
	constructor() {
		super();

		/** @type {BYOBStateManager} */
		this.stateManager = BYOBStateManager.getInstance();

		/** @type {BYOBDiscountCalculator|null} */
		this.discountCalculator = null;

		/** @type {BYOBCartSubmitter|null} */
		this.cartSubmitter = null;

		/** @type {Object.<number, number>} */
		this.discountTiers = {};

		/** @type {Function|null} */
		this.unsubscribe = null;

		/** @type {string|null} */
		this.sellingPlanId = null;

		/** @type {Object.<string, HTMLElement>} */
		this.refs = {};

		/** @type {BundleConfig} */
		this.config = {
			boxProductId: "",
			boxId: "",
			boxVariantId: "",
			minItems: 2,
			maxItems: 6,
			freeShippingThreshold: 0,
			storefrontToken: "",
			boxSellingPlanId: "",
			mainSellingPlanId: "",
		};

		/** @type {{ currencyCode?: string, categories: Array<{title?: string, subtitle?: string, navigationTitle?: string, isMainProducts?: boolean, productHandles?: string[]}>, images: Object.<string, string>, details: Object.<string, {feature?: string, benefits?: Array<{text?: string, icon?: string}>, variants?: Array<{id: string|number, title?: string, price?: number, basePrice?: number, customSellingPlanId?: string|null, customSellingPlanPrice?: number|null, compareAtPrice?: number|null, image?: string, imageCount?: number, saveBadge?: string|null, available?: boolean, inventory?: number, inventoryPolicy?: string|null}>, images?: Array<{src?: string, alt?: string}>}>, sellingPlans: Array<{id: string, name: string, description?: string, groupName?: string}>, reviews: Object.<string, {rating: number, count: number}>, variantImages?: Object.<string, string> }} */
		this.productConfig = {
			categories: [],
			images: {},
			details: {},
			sellingPlans: [],
			reviews: {},
			variantImages: {},
		};

		/** @type {Map<string, string>} */
		this.variantSellingPlanLookup = new Map();

		/** @type {string} */
		this.sectionId = "";
	}

	/**
	 * Called when element is connected to the DOM
	 * @returns {void}
	 */
	connectedCallback() {
		this.loadBundleConfig();
		this.loadProductConfig();
		this.initializeProductModal();
		this.initializeElements();
		this.initializeEventListeners();
		this.classList.add("is-loading");

		// Subscribe to state changes
		this.unsubscribe = this.stateManager.subscribe(() =>
			this.onStateChange(),
		);

		// Fetch products and initialize
		this.fetchDynamicBoxProducts().catch((error) => {
			console.error(
				"[BundleProductList] Failed to fetch products from Skio:",
				error,
			);
			// Fallback to Storefront API
			if (this.config.storefrontToken) {
				this.fetchStorefrontProducts().catch((fallbackError) => {
					console.error(
						"[BundleProductList] Fallback fetch failed:",
						fallbackError,
					);
					this.markInitialLoadComplete();
					this.showError(
						"Failed to load products. Please refresh the page.",
					);
				});
			} else {
				this.markInitialLoadComplete();
				this.showError(
					"Failed to load products. Please refresh the page.",
				);
			}
		});

		// Initial UI update
		this.onStateChange();
	}

	/**
	 * Called when element is disconnected from the DOM
	 * @returns {void}
	 */
	disconnectedCallback() {
		if (this.unsubscribe) {
			this.unsubscribe();
			this.unsubscribe = null;
		}
	}

	/**
	 * Initialize product modal from template
	 * @returns {void}
	 */
	initializeProductModal() {
		const template = /** @type {HTMLTemplateElement|null} */ (
			document.getElementById("byob-product-modal-template")
		);

		if (!template) {
			console.warn(
				"[BundleProductList] Product modal template not found",
			);
			return;
		}

		// Check if modal already exists
		if (this.querySelector(".byob-product-modal")) return;

		const clone = template.content.cloneNode(true);
		this.appendChild(clone);
	}

	/**
	 * Load bundle configuration from data attributes
	 * @returns {void}
	 */
	loadBundleConfig() {
		this.config = {
			boxProductId: this.dataset.productId || "",
			boxId: this.dataset.boxId || "",
			boxVariantId: this.dataset.boxVariantId || "",
			minItems: parseInt(this.dataset.minItems, 10) || 2,
			maxItems: parseInt(this.dataset.maxItems, 10) || 6,
			freeShippingThreshold: this.parseMoneySettingToCents(
				this.dataset.freeShippingThreshold,
			),
			storefrontToken: this.dataset.storefrontToken || "",
			boxSellingPlanId: this.dataset.boxSellingPlanId || "",
			mainSellingPlanId: this.dataset.mainSellingPlanId || "",
		};

		if (!this.config.boxSellingPlanId || !this.config.mainSellingPlanId) {
			console.warn(
				"[BundleProductList] Missing Skio selling plan ID(s) — set them in the BYOB section settings (Skio Selling Plans).",
			);
		}
	}

	/**
	 * Parse a merchant-entered money setting into cents.
	 * Supports values like "7500", "75", "75.00", or "75,00".
	 * @param {string|undefined} value
	 * @returns {number}
	 */
	parseMoneySettingToCents(value) {
		const rawValue = String(value || "").trim();
		if (!rawValue) return 0;

		const normalizedValue = rawValue
			.replace(/[^\d,.-]/g, "")
			.replace(",", ".");
		const parsedValue = Number.parseFloat(normalizedValue);

		if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
			return 0;
		}

		const hasDecimalSeparator =
			normalizedValue.includes(".") || rawValue.includes(",");

		if (hasDecimalSeparator) {
			return Math.round(parsedValue * 100);
		}

		// Values like "49" almost always mean the major currency unit, not 49 cents.
		if (parsedValue <= 100) {
			return Math.round(parsedValue * 100);
		}

		return Math.round(parsedValue);
	}

	/**
	 * Load product config from Liquid JSON (categories + images)
	 * @returns {void}
	 */
	loadProductConfig() {
		const sectionElement = this.closest("section");
		const sectionId = sectionElement?.id?.replace("BYOB-", "") || "";
		this.sectionId = sectionId;

		if (!sectionId) return;

		const script = document.getElementById(
			`byob-product-config-${sectionId}`,
		);
		if (!script) return;

		try {
			this.productConfig = JSON.parse(script.textContent || "{}");
			this.variantSellingPlanLookup =
				this.buildVariantSellingPlanLookup();
		} catch (error) {
			console.warn(
				"[BundleProductList] Failed to parse product config:",
				error,
			);
			this.productConfig = {
				categories: [],
				images: {},
				details: {},
				sellingPlans: [],
				reviews: {},
				variantImages: {},
			};
			this.variantSellingPlanLookup = new Map();
		}
	}

	/**
	 * Build a lookup for variant-specific selling plans from Liquid config.
	 * @returns {Map<string, string>}
	 */
	buildVariantSellingPlanLookup() {
		return new Map();
	}

	/**
	 * Render delivery frequency options from selling plans
	 * @param {Array<Object>} sellingPlans - Selling plans from Liquid config (box_product)
	 * @returns {void}
	 */
	renderDeliveryOptions(sellingPlans) {
		const container = this.refs.deliveryFrequency;
		const options = this.refs.deliveryOptions;
		if (!container || !options) return;
		this.sellingPlanId = this.config.boxSellingPlanId || null;

		options.innerHTML = "";

		if (!Array.isArray(sellingPlans) || sellingPlans.length === 0) {
			container.hidden = true;
			return;
		}

		container.hidden = false;

		sellingPlans.forEach((plan, index) => {
			const button = document.createElement("button");
			button.type = "button";
			button.className = "byob-gamification__option";
			// @ts-ignore - plan may have platformId (Skio) or id (Liquid)
			button.dataset.sellingPlanId = plan.platformId || plan.id || "";

			// @ts-ignore - plan may have various label properties
			const label =
				plan.name ||
				plan.title ||
				plan.displayName ||
				plan.interval ||
				plan.platformId ||
				`Plan ${index + 1}`;

			button.textContent = label;

			if (button.dataset.sellingPlanId === this.sellingPlanId) {
				button.classList.add("is-active");
			}

			button.addEventListener("click", () => {
				options
					.querySelectorAll(".byob-gamification__option")
					.forEach((el) => el.classList.remove("is-active"));
				button.classList.add("is-active");

				this.sellingPlanId = this.config.boxSellingPlanId || null;
				if (this.cartSubmitter) {
					this.cartSubmitter.sellingPlanId =
						this.config.boxSellingPlanId || null;
				}

				this.updateButtonPrice();
			});

			options.appendChild(button);
		});

		if (this.cartSubmitter && this.sellingPlanId) {
			this.cartSubmitter.sellingPlanId = this.sellingPlanId;
		}
	}

	/**
	 * Get Liquid image override for a product handle
	 * @param {string} handle - Product handle
	 * @returns {string|null}
	 */
	getLiquidImageForHandle(handle) {
		if (!handle) return null;
		return this.productConfig?.images?.[handle] || null;
	}

	/**
	 * Normalize review count for display.
	 * Uses 8,261 as fallback and replaces legacy placeholder 188.
	 * @param {number|string|null|undefined} rawCount
	 * @returns {number}
	 */
	normalizeReviewCount(rawCount) {
		const parsed =
			typeof rawCount === "number"
				? rawCount
				: parseInt(String(rawCount || "0"), 10);

		if (!Number.isFinite(parsed) || parsed <= 0 || parsed === 188) {
			return 8261;
		}

		return parsed;
	}

	/**
	 * Get review data for a product handle
	 * @param {string} handle - Product handle
	 * @returns {{rating: number, count: number} | null}
	 */
	getReviewsForHandle(handle) {
		if (!handle) return null;
		const review = this.productConfig?.reviews?.[handle];
		if (!review) return null;

		return {
			rating:
				typeof review.rating === "number"
					? review.rating
					: parseFloat(String(review.rating || "0")) || 0,
			count: this.normalizeReviewCount(review.count),
		};
	}

	/**
	 * Get extended product details for a handle
	 * @param {string} handle
	 * @returns {Object|null}
	 */
	getProductDetails(handle) {
		if (!handle) return null;
		return this.productConfig?.details?.[handle] || null;
	}

	/**
	 * Get variant image URL for a product handle
	 * @param {string} handle - Product handle
	 * @returns {string|null}
	 */
	getVariantImageForHandle(handle) {
		if (!handle) return null;
		return this.productConfig?.variantImages?.[handle] || null;
	}

	/**
	 * Build variant drawer payload from product data + Liquid details
	 * @param {Object} productData
	 * @param {Object|null} detail
	 * @returns {Object}
	 */
	buildVariantDrawerData(productData, detail) {
		const handle =
			productData?.productHandle || productData?.handle || "";
		const variantImage = this.getVariantImageForHandle(handle);
		const reviews = this.getReviewsForHandle(handle);

		const variantsFromDetail = Array.isArray(detail?.variants)
			? detail.variants
			: [];
		const variantsFromCard = Array.isArray(productData?.variants)
			? productData.variants
			: [];

		const variants =
			variantsFromDetail.length > 0
				? variantsFromDetail.map((variant) => {
						const fallbackPrice =
							typeof variant.basePrice === "number"
								? variant.basePrice
								: typeof variant.price === "number"
									? variant.price
									: 0;
						const customSellingPlanPrice =
							typeof variant.customSellingPlanPrice ===
							"number"
								? variant.customSellingPlanPrice
								: null;
						const referencePrice =
							customSellingPlanPrice !== null
								? customSellingPlanPrice
								: fallbackPrice;

						return {
							id: String(variant.id),
							title: variant.title,
							price: referencePrice,
							basePrice: fallbackPrice,
							customSellingPlanId:
								variant.customSellingPlanId || null,
							customSellingPlanPrice,
							compareAtPrice: referencePrice,
							image: variantImage || variant.image,
							imageCount: variant.imageCount,
							saveBadge: variant.saveBadge,
							available: variant.available ?? true,
							inventory: variant.inventory ?? 9999,
							inventoryPolicy:
								variant.inventoryPolicy || "deny",
						};
					})
				: variantsFromCard.map((variant, index) => {
						const referencePrice =
							typeof variant.price === "number"
								? variant.price
								: 0;
						return {
							id: String(variant.platformId),
							title: variant.productVariantTitle,
							price: referencePrice,
							basePrice: referencePrice,
							customSellingPlanId: null,
							customSellingPlanPrice: null,
							compareAtPrice: referencePrice,
							image:
								variantImage ||
								variant.imageSrc ||
								productData.productImageSrc ||
								"",
							imageCount: index + 1,
							saveBadge: "",
							available: variant.available ?? true,
							inventory: variant.inventory ?? 9999,
							inventoryPolicy:
								variant.inventoryPolicy || "deny",
						};
					});

		return {
			productId: productData?.productPlatformId || "",
			handle,
			title: productData?.productTitle || "",
			image: variantImage || productData?.productImageSrc || "",
			currencyCode: this.productConfig?.currencyCode || "USD",
			discountTiers: this.discountTiers || {},
			feature: detail?.feature || "",
			benefits: Array.isArray(detail?.benefits) ? detail.benefits : [],
			images: Array.isArray(detail?.images) ? detail.images : [],
			variants,
			reviews: reviews
				? {
						...reviews,
						count: this.normalizeReviewCount(reviews.count),
					}
				: null,
		};
	}

	/**
	 * Determine if a product counts as a main product
	 * @param {string} handle - Product handle
	 * @returns {boolean}
	 */
	isMainProductHandle(handle) {
		if (!handle) return true;
		const categories = this.productConfig?.categories;
		if (!Array.isArray(categories) || categories.length === 0) {
			return true;
		}

		for (const category of categories) {
			const handles = category.productHandles || [];
			if (handles.includes(handle)) {
				return Boolean(category.isMainProducts);
			}
		}

		return true;
	}

	/**
	 * Render star SVGs for a rating
	 * @param {number} rating - Average rating (0-5)
	 * @returns {string}
	 */
	renderStars(rating) {
		const parsedRating =
			typeof rating === "number" ? rating : parseFloat(rating || "0");
		const safeRating = Number.isFinite(parsedRating) ? parsedRating : 0;
		const filledStars = Math.max(0, Math.min(5, Math.round(safeRating)));
		const starPath =
			"M15.1998 23.4888L24.099 28.86L21.7374 18.7368L29.5998 11.9256L19.2462 11.0472L15.1998 1.5L11.1534 11.0472L0.799805 11.9256L8.6622 18.7368L6.3006 28.86L15.1998 23.4888Z";

		return Array.from({ length: 5 }, (_, index) => {
			const isFilled = index < filledStars;
			const color = isFilled ? "#000000" : "rgba(0, 0, 0, 0.25)";
			return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 32 30" fill="none" aria-hidden="true">
  <path d="${starPath}" fill="${color}" stroke="${color}" stroke-width="1.6"/>
</svg>`;
		}).join("");
	}

	/**
	 * Round discount values for display only.
	 * Keeps pricing math untouched while normalizing UI labels.
	 * @param {number|null|undefined} discount
	 * @returns {number}
	 */
	formatDisplayDiscount(discount) {
		if (typeof discount !== "number" || !Number.isFinite(discount)) {
			return 0;
		}
		const rounded = Math.round(discount * 2) / 2;
		return Number.isInteger(rounded)
			? rounded
			: Number(rounded.toFixed(1));
	}

	/**
	 * Render gamification steps from discount tiers
	 * @returns {void}
	 */
	renderGamificationSteps() {
		const tiers = this.discountTiers || {};
		const thresholds = Object.keys(tiers)
			.map((key) => parseInt(key, 10))
			.filter((value) => Number.isFinite(value) && value > 0)
			.sort((a, b) => a - b)
			.slice(0, 2);

		const renderedThresholds =
			thresholds.length === 2 ? [null, ...thresholds] : thresholds;

		const stepMarkup = renderedThresholds
			.map((threshold) => {
				if (threshold === null) {
					return `
          <div class="byob-gamification__step byob-gamification__step--ghost" aria-hidden="true">
            <span class="byob-gamification__step-badge">&nbsp;</span>
            <span class="byob-gamification__step-label">&nbsp;</span>
          </div>
        `;
				}
				const label =
					threshold === 1 ? "1 item" : `${threshold} items`;
				return `
          <div class="byob-gamification__step" data-step="${threshold}">
            <span class="byob-gamification__step-badge">0%</span>
            <span class="byob-gamification__step-label">${label}</span>
          </div>
        `;
			})
			.join("");

		const containers = [
			this.refs.bundleSteps,
			this.refs.mobileBundleSteps,
		].filter(Boolean);

		containers.forEach((container) => {
			container.innerHTML = stepMarkup;
		});

		this.updateBundleProgress();
	}

	/**
	 * Initialize DOM element references
	 * @returns {void}
	 */
	initializeElements() {
		const refNames = [
			"productGrid",
			"sidebar",
			"selectedProducts",
			"emptyState",
			"totalPrice",
			"savings",
			"submitBtn",
			"itemCount",
			"shippingProgress",
			"discountProgress",
			// Gamification refs
			"deliveryFrequency",
			"deliveryOptions",
			"bundleProgress",
			"bundleProgressFill",
			"bundleSteps",
			"nextTierCta",
			"nextTierText",
			"buttonComparePrice",
			"buttonFinalPrice",
			"mobileButtonComparePrice",
			"mobileButtonFinalPrice",
			"freeGifts",
			"variantDrawer",
			// Mobile bar refs
			"mobileBar",
			"mobileItemCount",
			"mobileTotalPrice",
			"mobileSubmitBtn",
			"mobileBundleProgress",
			"mobileBundleProgressFill",
			"mobileBundleSteps",
			"mobileShippingProgress",
			// Mobile product thumbnails
			"mobileProducts",
			"mobileEmpty",
			// Product modal refs
			"productModal",
			"modalBackdrop",
			"modalClose",
			"modalImage",
			"modalTitle",
			"modalVariant",
			"modalPrice",
			"modalDecrease",
			"modalQuantity",
			"modalIncrease",
			"modalRemove",
			"modalDone",
			// Category navigation
			"categoryNav",
			"categoryNavList",
		];

		for (const refName of refNames) {
			const element = this.querySelector(`[data-ref="${refName}"]`);
			if (element) {
				this.refs[refName] = element;
			}
		}
	}

	/**
	 * Initialize event listeners
	 * @returns {void}
	 */
	initializeEventListeners() {
		// Listen for product card events
		this.addEventListener(
			"byob:product:add",
			this.handleProductAdded.bind(this),
		);
		this.addEventListener(
			"byob:variant:select",
			this.handleVariantSelect.bind(this),
		);
		this.addEventListener(
			"byob:variant:selected",
			this.handleVariantSelected.bind(this),
		);
		this.addEventListener(
			"byob:product:quantity",
			this.handleQuantityChanged.bind(this),
		);
		this.addEventListener(
			"byob:product:remove",
			this.handleProductRemoved.bind(this),
		);

		// Submit button
		if (this.refs.submitBtn) {
			this.refs.submitBtn.addEventListener("click", () =>
				this.addToCart(),
			);
		}

		// Mobile submit button
		if (this.refs.mobileSubmitBtn) {
			this.refs.mobileSubmitBtn.addEventListener("click", () =>
				this.addToCart(),
			);
		}

		// Sidebar item actions (delegated) - handles remove, increase, decrease
		if (this.refs.selectedProducts) {
			this.refs.selectedProducts.addEventListener("click", (event) => {
				const target = /** @type {HTMLElement} */ (event.target);
				const actionBtn = target.closest("[data-action]");
				if (!actionBtn) return;

				const action = actionBtn.dataset.action;
				const item = actionBtn.closest(".byob-sidecart-item");
				const rawVariantId =
					actionBtn.dataset.variantId ||
					item?.querySelector("[data-variant-id]")?.dataset
						.variantId;

				if (!rawVariantId) return;
				const variantId = String(rawVariantId);

				if (action === "remove") {
					this.stateManager.removeProduct(variantId);
					document.dispatchEvent(
						new CustomEvent("byob:product:removed", {
							detail: { variantId },
						}),
					);
				} else if (action === "decrease") {
					const product =
						this.stateManager.getProduct(variantId);
					if (product && product.quantity > 1) {
						const newQuantity = product.quantity - 1;
						this.stateManager.updateQuantity(
							variantId,
							newQuantity,
						);
						document.dispatchEvent(
							new CustomEvent("byob:quantity:update", {
								detail: {
									variantId,
									quantity: newQuantity,
								},
							}),
						);
					} else if (product && product.quantity === 1) {
						// Remove if quantity would go to 0
						this.stateManager.removeProduct(variantId);
						document.dispatchEvent(
							new CustomEvent("byob:product:removed", {
								detail: { variantId },
							}),
						);
					}
				} else if (action === "increase") {
					const product =
						this.stateManager.getProduct(variantId);
					const totalCount = this.stateManager.getTotalCount();
					// Check both max items limit and inventory limit
					const inventoryLimit = product?.inventory ?? 9999;
					const canContinueSelling =
						String(
							product?.inventoryPolicy || "deny",
						).toLowerCase() === "continue";
					if (
						product &&
						totalCount < this.config.maxItems &&
						(canContinueSelling ||
							product.quantity < inventoryLimit)
					) {
						const newQuantity = product.quantity + 1;
						this.stateManager.updateQuantity(
							variantId,
							newQuantity,
						);
						document.dispatchEvent(
							new CustomEvent("byob:quantity:update", {
								detail: {
									variantId,
									quantity: newQuantity,
								},
							}),
						);
					}
				}
			});

			// Handle quantity input changes
			this.refs.selectedProducts.addEventListener(
				"change",
				(event) => {
					const target = /** @type {HTMLInputElement} */ (
						event.target
					);
					if (target.matches('[data-ref="quantityInput"]')) {
						const item = target.closest(
							".byob-sidecart-item",
						);
						const rawVariantId =
							item?.querySelector("[data-variant-id]")
								?.dataset.variantId;
						if (!rawVariantId) return;
						const variantId = String(rawVariantId);

						const newQuantity =
							parseInt(target.value, 10) || 0;
						const product =
							this.stateManager.getProduct(variantId);
						if (!product) return;

						if (newQuantity <= 0) {
							this.stateManager.removeProduct(variantId);
							document.dispatchEvent(
								new CustomEvent(
									"byob:product:removed",
									{
										detail: { variantId },
									},
								),
							);
						} else {
							const totalCount =
								this.stateManager.getTotalCount();
							const delta = newQuantity - product.quantity;
							const inventoryLimit =
								product.inventory ?? 9999;
							const canContinueSelling =
								String(
									product.inventoryPolicy || "deny",
								).toLowerCase() === "continue";
							// Check both max items and inventory limits
							if (
								totalCount + delta <=
									this.config.maxItems &&
								(canContinueSelling ||
									newQuantity <= inventoryLimit)
							) {
								this.stateManager.updateQuantity(
									variantId,
									newQuantity,
								);
								document.dispatchEvent(
									new CustomEvent(
										"byob:quantity:update",
										{
											detail: {
												variantId,
												quantity:
													newQuantity,
											},
										},
									),
								);
							} else {
								// Reset to max allowed (min of current or inventory limit)
								const maxAllowed = canContinueSelling
									? product.quantity
									: Math.min(
											product.quantity,
											inventoryLimit,
										);
								target.value = String(maxAllowed);
							}
						}
					}
				},
			);

			this.refs.selectedProducts.addEventListener("scroll", () =>
				this.updateSidebarProductsFade(),
			);
		}

		// Product modal event listeners
		if (this.refs.modalBackdrop) {
			this.refs.modalBackdrop.addEventListener(
				"click",
				this.closeProductModal.bind(this),
			);
		}

		if (this.refs.modalClose) {
			this.refs.modalClose.addEventListener(
				"click",
				this.closeProductModal.bind(this),
			);
		}

		if (this.refs.modalDone) {
			this.refs.modalDone.addEventListener(
				"click",
				this.closeProductModal.bind(this),
			);
		}

		if (this.refs.modalDecrease) {
			this.refs.modalDecrease.addEventListener(
				"click",
				this.decreaseModalQuantity.bind(this),
			);
		}

		if (this.refs.modalIncrease) {
			this.refs.modalIncrease.addEventListener(
				"click",
				this.increaseModalQuantity.bind(this),
			);
		}

		if (this.refs.modalRemove) {
			this.refs.modalRemove.addEventListener(
				"click",
				this.removeModalProduct.bind(this),
			);
		}

		// Mobile products click delegation for thumbnail clicks
		if (this.refs.mobileProducts) {
			this.refs.mobileProducts.addEventListener("click", (event) => {
				const target = /** @type {HTMLElement|null} */ (
					event.target instanceof HTMLElement
						? event.target
						: null
				);
				if (!target) return;

				const addCard = target.closest(
					".byob-mobile-bar__add-card",
				);
				if (addCard) {
					this.refs.productGrid?.scrollIntoView({
						behavior: "smooth",
					});
					return;
				}

				const thumb = target.closest(
					".byob-mobile-bar__product-thumb",
				);
				if (
					thumb &&
					thumb.dataset.variantId &&
					!thumb.dataset.freeGift
				) {
					this.openProductModal(thumb.dataset.variantId);
				}
			});
		}

		// Escape key to close modal
		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				const modal = this.refs.productModal;
				if (
					modal &&
					modal.classList.contains("byob-product-modal--open")
				) {
					this.closeProductModal();
				}
			}
		});
	}

	/**
	 * Returns true when UI is in mobile viewport.
	 * @returns {boolean}
	 */
	isMobileViewport() {
		return window.matchMedia("(max-width: 768px)").matches;
	}

	/**
	 * Fetch products from Skio Dynamic Box API
	 * @returns {Promise<void>}
	 */
	async fetchDynamicBoxProducts() {
		console.log("[BYOB] Starting Skio fetch...");
		console.log("[BYOB] Config:", this.config);
		console.log("[BYOB] Product config from Liquid:", this.productConfig);

		if (!this.config.boxProductId) {
			throw new Error("Missing box product ID");
		}

		const productPlatformId = this.config.boxProductId.startsWith(
			"gid://",
		)
			? this.config.boxProductId.split("/").pop()
			: this.config.boxProductId;

		const payload = {
			productPlatformId,
			countryCode:
				this.dataset.countryCode ||
				window.Shopify?.country?.iso_code ||
				"",
			region: this.dataset.skioRegion || "us-west-2",
			userPoolId: this.dataset.skioUserPoolId || "us-west-2_ky2hNEMAZ",
		};

		console.log("[BYOB] Skio API payload:", payload);

		const response = await fetch(
			"https://api.skio.com/storefront-http/get-dynamic-box",
			{
				method: "POST",
				headers: {
					Accept: "*/*",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			},
		);

		if (!response.ok) {
			throw new Error(`Skio API error: ${response.status}`);
		}

		const data = await response.json();
		const organized = this.organizeSkioData(data);

		// Initialize discount calculator with tiers
		this.discountCalculator = new BYOBDiscountCalculator(
			organized.discountTiers,
		);
		this.discountTiers = organized.discountTiers || {};
		this.renderGamificationSteps();
		this.updateNextTierCta();

		// Render delivery frequency options from Liquid config
		const liquidSellingPlans = this.productConfig?.sellingPlans || [];
		this.renderDeliveryOptions(liquidSellingPlans);

		// Fallback to Skio selling plan ID if no Liquid plans
		if (!this.sellingPlanId && organized.sellingPlanId) {
			this.sellingPlanId = organized.sellingPlanId;
		}
		this.sellingPlanId = this.config.boxSellingPlanId || null;

		// Store box ID from response if missing in config
		if (!this.config.boxId && organized.boxId) {
			this.config.boxId = organized.boxId;
		}

		// Initialize cart submitter
		this.cartSubmitter = new BYOBCartSubmitter({
			boxId: this.config.boxId,
			boxVariantId: this.config.boxVariantId,
			sellingPlanId: this.config.boxSellingPlanId || null,
			onSuccess: () => this.handleCartSuccess(),
			onError: (error) => this.showError(error),
		});

		// Render products
		this.renderProductCards(organized.groupedProducts);
		this.markInitialLoadComplete();

		// Re-render sidebar with correct prices now that discountCalculator is initialized
		this.onStateChange();
	}

	/**
	 * Fallback: Fetch products from Shopify Storefront API
	 * @returns {Promise<void>}
	 */
	async fetchStorefrontProducts() {
		if (!this.config.storefrontToken) {
			throw new Error("Missing Storefront API token");
		}

		const query = `
      query GetBundleProducts($handle: String!) {
        collection(handle: $handle) {
          products(first: 50) {
            nodes {
              id
              title
              handle
              description
              featuredImage {
                url
                altText
              }
              variants(first: 10) {
                nodes {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                }
              }
              metafield(namespace: "byob", key: "category") {
                value
              }
            }
          }
        }
      }
    `;

		const response = await fetch(
			`https://${
				window.Shopify?.shop || window.location.hostname
			}/api/2024-01/graphql.json`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Shopify-Storefront-Access-Token":
						this.config.storefrontToken,
				},
				body: JSON.stringify({
					query,
					variables: { handle: "byob-products" },
				}),
			},
		);

		if (!response.ok) {
			throw new Error(`Storefront API error: ${response.status}`);
		}

		const data = await response.json();

		if (data.errors) {
			throw new Error(data.errors[0]?.message || "GraphQL error");
		}

		const products = data.data?.collection?.products?.nodes || [];

		// Group products by category metafield
		const grouped = this.groupProductsByCategory(products);

		// Initialize with default discount tiers
		this.discountCalculator = new BYOBDiscountCalculator({});
		this.discountTiers = {};
		this.renderGamificationSteps();

		// Initialize cart submitter
		this.cartSubmitter = new BYOBCartSubmitter({
			boxId: this.config.boxId,
			boxVariantId: this.config.boxVariantId,
			sellingPlanId: this.config.boxSellingPlanId || null,
			onSuccess: () => this.handleCartSuccess(),
			onError: (error) => this.showError(error),
		});

		this.renderProductCards(grouped);
		this.markInitialLoadComplete();

		// Render delivery frequency options from Liquid config
		const liquidSellingPlans = this.productConfig?.sellingPlans || [];
		this.renderDeliveryOptions(liquidSellingPlans);

		// Re-render sidebar with correct prices now that discountCalculator is initialized
		this.onStateChange();
	}

	/**
	 * Group Storefront API products by category
	 * @param {Array<Object>} products - Products from Storefront API
	 * @returns {GroupedProducts[]}
	 */
	groupProductsByCategory(products) {
		const categoryMap = new Map();

		for (const product of products) {
			const category = product.metafield?.value || "Other";

			if (!categoryMap.has(category)) {
				categoryMap.set(category, []);
			}

			// Transform to Skio-like format
			categoryMap.get(category).push({
				platformId: product.id,
				title: product.title,
				handle: product.handle,
				description: product.description,
				featuredImage: product.featuredImage,
				variants:
					product.variants?.nodes?.map((v) => ({
						platformId: v.id,
						title: v.title,
						price: v.price,
						compareAtPrice: v.compareAtPrice,
						image: v.image,
					})) || [],
			});
		}

		return Array.from(categoryMap.entries()).map(
			([category, products]) => ({
				category,
				products,
			}),
		);
	}

	/**
	 * Organize Skio API response data
	 * @param {Object} data - Raw Skio API response
	 * @returns {{ discountTiers: Object.<number, number>, groupedProducts: GroupedProducts[], sellingPlanId: string|null, sellingPlans: Array<Object>, boxId: string|null }}
	 */
	organizeSkioData(data) {
		const box = data?.DynamicBox;
		if (!box) {
			throw new Error("Invalid Skio response: missing DynamicBox");
		}

		// Extract quantity thresholds from Skio, but keep BYOB discount values
		// hard-coded in the frontend so pricing and UI stay stable.
		const discountTiers = {};
		const percentTier = box.percentDiscountTier || {};
		const fixedTier = box.fixedDiscountTier || {};
		const tierEntries =
			Object.keys(percentTier).length > 0
				? Object.entries(percentTier)
				: Object.entries(fixedTier);

		const thresholds = [];
		for (const [quantity] of tierEntries) {
			const quantityNumber = parseInt(quantity, 10);
			if (!Number.isNaN(quantityNumber)) {
				thresholds.push(quantityNumber);
			}
		}

		thresholds
			.sort((a, b) => a - b)
			.slice(0, 2)
			.forEach((threshold, index) => {
				discountTiers[threshold] = index === 0 ? 12.49 : 24.99;
			});

		// Extract selling plan ID from sellingPlanGroup
		let sellingPlanId = null;
		const sellingPlanGroup = box.sellingPlanGroup || {};
		const sellingPlans = Object.entries(sellingPlanGroup).map(
			([key, value]) => ({
				platformId: key.replace("gid://shopify/SellingPlan/", ""),
				interval: value.interval,
				intervalCount: value.intervalCount,
			}),
		);
		if (sellingPlans.length > 0) {
			sellingPlanId = sellingPlans[0]?.platformId || null;
		} else if (Object.keys(sellingPlanGroup).length === 0) {
			console.warn(
				"[BundleProductList] No selling plan found in Skio response. Subscription-only flow may fail.",
			);
		}

		console.log({ sellingPlanId, box });

		// Transform products into expected format
		const currencyCode = box.currencyCode || "USD";
		console.log("[BYOB] Skio box data received:", box);
		console.log(
			"[BYOB] Skio selectableProductVariants count:",
			(box.selectableProductVariants || []).length,
		);
		console.log(
			"[BYOB] Skio oneTimeUpsells count:",
			(box.oneTimeUpsells || []).length,
		);
		console.log(
			"[BYOB] Product images config:",
			this.productConfig?.images,
		);

		/**
		 * Transform a Skio product object into our internal format
		 * @param {Object} product - Product from Skio API
		 * @returns {Object} Transformed product
		 */
		const transformProduct = (product) => {
			const liquidImage = this.getLiquidImageForHandle(
				product.productHandle,
			);
			const featuredImageUrl =
				liquidImage || product.productImageSrc || null;

			if (liquidImage) {
				console.log(
					"[BYOB] Custom image found for:",
					product.productHandle,
					"->",
					liquidImage,
				);
			}

			return {
				platformId: product.productPlatformId,
				title: product.productTitle,
				handle: product.productHandle,
				description: "",
				featuredImage: featuredImageUrl
					? {
							url: featuredImageUrl,
							altText: product.productTitle,
						}
					: null,
				variants: (product.productVariants || []).map(
					(variant) => ({
						platformId: variant.platformId,
						title: variant.productVariantTitle,
						price: {
							amount: String(variant.price ?? "0"),
							currencyCode,
						},
						compareAtPrice:
							variant.compareAtPrice != null
								? {
										amount: String(
											variant.compareAtPrice,
										),
										currencyCode,
									}
								: null,
						image: variant.imageSrc
							? {
									url: variant.imageSrc,
									altText: product.productTitle,
								}
							: null,
					}),
				),
			};
		};

		// Combine selectableProductVariants and oneTimeUpsells
		const selectableProducts = (box.selectableProductVariants || []).map(
			transformProduct,
		);
		const upsellProducts = (box.oneTimeUpsells || []).map(
			transformProduct,
		);

		// Merge products, avoiding duplicates by handle
		const productMap = new Map();
		for (const product of selectableProducts) {
			productMap.set(product.handle, product);
		}
		for (const product of upsellProducts) {
			if (!productMap.has(product.handle)) {
				productMap.set(product.handle, product);
			}
		}
		const products = Array.from(productMap.values());

		console.log(
			"[BYOB] Total unique products after combining:",
			products.length,
		);

		let groupedProducts = [];

		if (this.productConfig?.categories?.length) {
			const productByHandle = new Map(
				products.map((product) => [product.handle, product]),
			);

			console.log(
				"[BYOB] Categories from Liquid:",
				this.productConfig.categories,
			);
			console.log(
				"[BYOB] Products from Skio (by handle):",
				Array.from(productByHandle.keys()),
			);

			groupedProducts = this.productConfig.categories
				.map((categoryConfig) => {
					const handles = categoryConfig.productHandles || [];
					const categoryProducts = handles
						.map((handle) => productByHandle.get(handle))
						.filter(Boolean);

					console.log(
						"[BYOB] Category:",
						categoryConfig.title,
						"Handles:",
						handles,
						"Found products:",
						categoryProducts.length,
					);

					return {
						category: categoryConfig.title,
						subtitle: categoryConfig.subtitle,
						navigationTitle: categoryConfig.navigationTitle,
						isMainProducts: categoryConfig.isMainProducts,
						products: categoryProducts,
					};
				})
				.filter((group) => group.products.length > 0);

			console.log(
				"[BYOB] Final grouped products:",
				groupedProducts.length,
				"categories",
			);

			// If no products matched, fall back to showing all Skio products
			if (groupedProducts.length === 0 && products.length > 0) {
				console.warn(
					"[BYOB] No products matched Liquid handles. Falling back to Skio products.",
				);
				groupedProducts = [
					{
						category: "Products",
						subtitle: "",
						navigationTitle: "All",
						isMainProducts: true,
						products: products,
					},
				];
			}
		}

		if (groupedProducts.length === 0) {
			// Group products by Skio category settings
			const settings = box.settings || {};
			const productGrouping = settings.productGrouping || {};
			const groupingOrder = settings.productGroupingOrder || [];

			// Create category map
			const categoryMap = new Map();

			// Initialize categories in order
			for (const category of groupingOrder) {
				categoryMap.set(category, []);
			}

			// Assign products to categories
			for (const product of products) {
				let category = "Other";
				const numericProductId = product.platformId
					?.split("/")
					.pop();

				// Find category from productGrouping using numeric product IDs
				for (const [cat, productIds] of Object.entries(
					productGrouping,
				)) {
					if (
						Array.isArray(productIds) &&
						numericProductId &&
						productIds.includes(numericProductId)
					) {
						category = cat;
						break;
					}
				}

				if (!categoryMap.has(category)) {
					categoryMap.set(category, []);
				}

				categoryMap.get(category).push(product);
			}

			// Convert to array, maintaining order
			const orderedGroups = [];

			for (const category of groupingOrder) {
				const categoryProducts = categoryMap.get(category);
				if (categoryProducts && categoryProducts.length > 0) {
					orderedGroups.push({
						category,
						products: categoryProducts,
					});
				}
			}

			// Add any remaining categories not in order
			for (const [
				category,
				categoryProducts,
			] of categoryMap.entries()) {
				if (
					!groupingOrder.includes(category) &&
					categoryProducts.length > 0
				) {
					orderedGroups.push({
						category,
						products: categoryProducts,
					});
				}
			}

			groupedProducts = orderedGroups;
		}

		return {
			discountTiers,
			groupedProducts,
			sellingPlanId,
			sellingPlans,
			boxId: box.boxId || null,
		};
	}

	/**
	 * Render product cards from grouped products
	 * @param {GroupedProducts[]} groupedProducts - Products grouped by category
	 * @returns {void}
	 */
	renderProductCards(groupedProducts) {
		console.log(
			"[BYOB] renderProductCards called with:",
			groupedProducts?.length,
			"groups",
		);
		console.log("[BYOB] Groups:", groupedProducts);

		const grid = this.refs.productGrid;
		if (!grid) {
			console.error("[BYOB] Product grid element not found");
			return;
		}

		const template = /** @type {HTMLTemplateElement|null} */ (
			document.getElementById("byob-product-card-template")
		);

		if (!template) {
			console.error(
				"[BundleProductList] Product card template not found",
			);
			return;
		}

		// Clear existing content
		grid.innerHTML = "";

		for (const group of groupedProducts) {
			// Create category header with ID for scroll targeting
			const header = document.createElement("h2");
			header.className = "byob-category-header";
			header.id = `byob-category-${group.category
				.toLowerCase()
				.replace(/\s+/g, "-")}`;
			header.textContent = group.category;
			grid.appendChild(header);

			if (group.subtitle) {
				const subtitle = document.createElement("p");
				subtitle.className = "byob-category-subtitle";
				subtitle.textContent = group.subtitle;
				grid.appendChild(subtitle);
			}

			// Create category container
			const categoryContainer = document.createElement("div");
			categoryContainer.className = "byob-category-products";

			for (const product of group.products) {
				const clone = /** @type {DocumentFragment} */ (
					template.content.cloneNode(true)
				);
				const card = /** @type {HTMLElement|null} */ (
					clone.querySelector("byob-product-card")
				);

				if (card) {
					// Store product data temporarily on the card element
					card._pendingProductData = product;
					categoryContainer.appendChild(clone);
				}
			}

			grid.appendChild(categoryContainer);

			// Now initialize all cards after they're in the DOM (connectedCallback has fired)
			const cards =
				categoryContainer.querySelectorAll("byob-product-card");
			cards.forEach((card) => {
				if (card._pendingProductData) {
					this.initializeProductCard(
						card,
						card._pendingProductData,
					);
					delete card._pendingProductData;
				}
			});
		}

		// Populate category navigation
		this.populateCategoryNav(groupedProducts);

		// Align sidebar with the first row of product cards.
		this.alignSidebarWithFirstCategoryRow();
	}

	/**
	 * Set sidebar top margin to align with the first category products row.
	 * margin-top = top(first .byob-category-products) - top(.byob-product-grid)
	 * @returns {void}
	 */
	alignSidebarWithFirstCategoryRow() {
		const sidebar = this.refs.sidebar;
		const grid = this.refs.productGrid;
		if (
			!(sidebar instanceof HTMLElement) ||
			!(grid instanceof HTMLElement)
		) {
			return;
		}

		window.requestAnimationFrame(() => {
			const firstCategoryRow = grid.querySelector(
				".byob-category-products",
			);
			if (!(firstCategoryRow instanceof HTMLElement)) {
				sidebar.style.marginTop = "";
				return;
			}

			const gridTop = grid.getBoundingClientRect().top;
			const firstRowTop = firstCategoryRow.getBoundingClientRect().top;
			const marginTop = Math.max(0, Math.round(firstRowTop - gridTop));
			sidebar.style.marginTop = `${marginTop}px`;
		});
	}

	/**
	 * Populate the category navigation with buttons for each category
	 * @param {GroupedProducts[]} groupedProducts - Products grouped by category
	 * @returns {void}
	 */
	populateCategoryNav(groupedProducts) {
		const navList = this.refs.categoryNavList;
		if (!navList) return;

		// Keep the "All" button, remove dynamically added category buttons
		const existingItems = navList.querySelectorAll(
			".byob-category-nav__item:not(:first-child)",
		);
		existingItems.forEach((item) => item.remove());

		// Add category buttons
		for (const group of groupedProducts) {
			const li = document.createElement("li");
			li.className = "byob-category-nav__item";

			const btn = document.createElement("button");
			btn.type = "button";
			btn.className = "byob-category-nav__btn";
			btn.dataset.category = group.category
				.toLowerCase()
				.replace(/\s+/g, "-");
			btn.textContent = group.navigationTitle || group.category;

			li.appendChild(btn);
			navList.appendChild(li);
		}

		// Add click handler for navigation
		navList.addEventListener("click", (e) => {
			const btn = /** @type {HTMLElement} */ (e.target).closest(
				".byob-category-nav__btn",
			);
			if (!btn) return;

			// Update active state
			navList
				.querySelectorAll(".byob-category-nav__btn")
				.forEach((b) => b.classList.remove("is-active"));
			btn.classList.add("is-active");

			const category = btn.dataset.category;
			if (category === "all") {
				// Scroll to top of product grid
				this.refs.productGrid?.scrollIntoView({
					behavior: "smooth",
				});
			} else {
				// Scroll to category header
				const header = document.getElementById(
					`byob-category-${category}`,
				);
				if (header) {
					header.scrollIntoView({ behavior: "smooth" });
				}
			}
		});
	}

	/**
	 * Initialize a product card with product data
	 * @param {HTMLElement} card - The byob-product-card element
	 * @param {Object} product - Product data from organizeSkioData
	 * @returns {void}
	 */
	initializeProductCard(card, product) {
		const isMainProduct = this.isMainProductHandle(product.handle);

		// Set data attributes for the card
		card.dataset.productId = product.platformId;
		card.dataset.handle = product.handle;
		card.dataset.maxItems = String(this.config.maxItems);
		card.dataset.currencyCode = this.productConfig?.currencyCode || "USD";
		card.dataset.isMainProduct = isMainProduct ? "true" : "false";

		// Set image manually for initial render
		const image = /** @type {HTMLImageElement|null} */ (
			card.querySelector('[data-ref="image"]')
		);
		const customImage = this.getLiquidImageForHandle(product.handle);
		const productImage = customImage || product.featuredImage?.url || "";
		if (image && productImage) {
			image.src = productImage;
			image.alt = product.featuredImage?.altText || product.title;
		}

		// Set title manually for initial render
		const title = card.querySelector('[data-ref="title"]');
		if (title) {
			title.textContent = product.title;
		}

		// Set tagline/description
		const tagline = card.querySelector('[data-ref="tagline"]');
		if (tagline && product.description) {
			tagline.textContent = product.description;
		}

		// Set discount badge based on the current bundle tier
		const discountBadge = card.querySelector(
			'[data-ref="discountBadge"]',
		);

		if (discountBadge instanceof HTMLElement && this.discountCalculator) {
			const mainCount = this.stateManager.getMainProductCount();
			const tier =
				isMainProduct && mainCount > 0
					? this.discountCalculator.getDisplayTier(mainCount)
					: null;
			if (tier !== null) {
				discountBadge.textContent = `-${this.formatDisplayDiscount(tier)}%`;
				discountBadge.hidden = false;
			} else {
				discountBadge.hidden = true;
			}
		} else if (discountBadge instanceof HTMLElement) {
			discountBadge.hidden = true;
		}

		// Set review stars and count
		const reviewData = this.getReviewsForHandle(product.handle);
		console.log("[BYOB] Reviews data:", {
			handle: product.handle,
			reviewData,
		});
		const reviewsEl = card.querySelector('[data-ref="reviews"]');
		const starsEl = card.querySelector('[data-ref="stars"]');
		const countEl = card.querySelector('[data-ref="reviewCount"]');

		if (reviewsEl instanceof HTMLElement) {
			if (reviewData && (reviewData.rating || reviewData.count)) {
				if (starsEl instanceof HTMLElement) {
					starsEl.innerHTML = this.renderStars(
						reviewData.rating,
					);
				}
				if (countEl instanceof HTMLElement) {
					const countValue = this.normalizeReviewCount(
						reviewData.count,
					);
					countEl.textContent =
						countValue > 0
							? `(${countValue.toLocaleString("en-US")})`
							: "";
				}
				reviewsEl.hidden = false;
			} else {
				reviewsEl.hidden = true;
			}
		}

		// Get variant data from Liquid config (source of truth for price/currency/inventory)
		const liquidDetails = this.productConfig?.details?.[product.handle];
		const liquidVariants = liquidDetails?.variants || [];
		const firstLiquidVariant = liquidVariants[0] || null;
		const firstLiquidVariantPrice =
			firstLiquidVariant &&
			typeof firstLiquidVariant.basePrice === "number"
				? firstLiquidVariant.basePrice
				: firstLiquidVariant &&
					  typeof firstLiquidVariant.price === "number"
					? firstLiquidVariant.price
					: null;
		const liquidVariantMap = new Map();
		liquidVariants.forEach((v) => {
			console.log("[BYOB][InventoryPolicy][LiquidVariant]", {
				handle: product.handle,
				variantId: String(v.id),
				available: v.available,
				inventory: v.inventory,
				inventoryPolicy: v.inventoryPolicy,
			});
			liquidVariantMap.set(String(v.id), {
				available: v.available ?? true,
				inventory: v.inventory ?? 9999,
				inventoryPolicy: v.inventoryPolicy || "deny",
				price: typeof v.price === "number" ? v.price : null,
				compareAtPrice:
					typeof v.compareAtPrice === "number"
						? v.compareAtPrice
						: null,
			});
		});

		// Transform product data to the format expected by byob-product-card.initializeCard()
		const cardProductData = {
			productPlatformId: product.platformId,
			productTitle: product.title,
			productHandle: product.handle,
			isMainProduct,
			// Prefer metafield-provided product image (Liquid config) before falling back.
			productImageSrc: productImage || "",
			currencyCode: this.productConfig?.currencyCode || "USD",
			// Prefer Liquid variants list as the canonical set/order for pricing.
			// Skio variants can be a subset and can come back in a non-deterministic order.
			variants:
				Array.isArray(liquidVariants) && liquidVariants.length > 0
					? liquidVariants.map((lv) => {
							const fallbackPrice =
								typeof lv.basePrice === "number"
									? lv.basePrice
									: typeof lv.price === "number"
										? lv.price
										: 0;
							const customSellingPlanPrice =
								typeof lv.customSellingPlanPrice ===
								"number"
									? lv.customSellingPlanPrice
									: null;
							const referencePrice =
								customSellingPlanPrice !== null
									? customSellingPlanPrice
									: fallbackPrice;
							const compareAtPrice = isMainProduct
								? (firstLiquidVariantPrice ??
									fallbackPrice)
								: referencePrice;

							return {
								platformId: String(lv.id),
								productVariantTitle:
									lv.title || "Default Title",
								price: referencePrice,
								compareAtPrice,
								basePrice: fallbackPrice,
								customSellingPlanPrice,
								customSellingPlanId:
									lv.customSellingPlanId || null,
								imageSrc:
									lv.image ||
									product.featuredImage?.url ||
									"",
								inventory: lv.inventory ?? 9999,
								inventoryPolicy:
									lv.inventoryPolicy || "deny",
								available: lv.available ?? true,
							};
						})
					: (product.variants || []).map((v) => {
							const variantId = String(
								v.platformId,
							).replace(
								"gid://shopify/ProductVariant/",
								"",
							);
							// Normalize IDs to numeric strings everywhere (Skio/Storefront may return GIDs).
							const normalizedVariantId =
								variantId || String(v.platformId || "");
							const liquidData =
								liquidVariantMap.get(
									normalizedVariantId,
								);
							const priceFromSkio = Math.round(
								parseFloat(v.price?.amount || "0") *
									100,
							);
							const firstProductVariant =
								(product.variants || [])[0] || null;
							const firstProductVariantPrice =
								firstLiquidVariantPrice ??
								(firstProductVariant?.price?.amount
									? Math.round(
											parseFloat(
												firstProductVariant
													.price.amount,
											) * 100,
										)
									: null);
							const compareFromSkio = v.compareAtPrice
								?.amount
								? Math.round(
										parseFloat(
											v.compareAtPrice.amount,
										) * 100,
									)
								: null;

							return {
								platformId: normalizedVariantId,
								productVariantTitle:
									v.title || "Default Title",
								// Liquid config is source of truth; fallback to Skio amounts if missing.
								price:
									liquidData?.price ?? priceFromSkio,
								compareAtPrice: isMainProduct
									? (firstProductVariantPrice ??
										liquidData?.compareAtPrice ??
										compareFromSkio ??
										priceFromSkio)
									: (liquidData?.compareAtPrice ??
										compareFromSkio),
								imageSrc:
									v.image?.url ||
									product.featuredImage?.url ||
									"",
								inventory:
									liquidData?.inventory ?? 9999,
								inventoryPolicy:
									liquidData?.inventoryPolicy ||
									"deny",
								available:
									liquidData?.available ?? true,
							};
						}),
		};

		// Call the card's initialization method with transformed data
		if (typeof card.initializeCard === "function") {
			card.initializeCard(cardProductData, this.discountCalculator);
		}
	}

	/**
	 * Handle product added event from card
	 * @param {CustomEvent} event - The add event
	 * @returns {void}
	 */
	handleProductAdded(event) {
		const {
			productId,
			variantId,
			title,
			handle,
			variantTitle,
			price,
			compareAtPrice,
			image,
			inventory,
			inventoryPolicy,
			customSellingPlanId,
			usesCustomSellingPlan,
		} = event.detail;

		// Check max items limit
		const currentCount = this.stateManager.getTotalCount();
		if (currentCount >= this.config.maxItems) {
			if (!this.isMobileViewport()) {
				this.showError(
					`Maximum of ${this.config.maxItems} items allowed in your bundle.`,
				);
			}
			return;
		}

		// Prioritize custom product image from Liquid config over variant image
		const customImage = this.getLiquidImageForHandle(handle);
		const finalImage = customImage || image || "";

		this.stateManager.addProduct({
			productId,
			variantId: String(variantId),
			title,
			handle,
			variantTitle: variantTitle || "",
			price: String(price),
			compareAtPrice: compareAtPrice ? String(compareAtPrice) : null,
			quantity: 1,
			image: finalImage,
			isMainProduct: this.isMainProductHandle(handle),
			inventory: inventory ?? 9999,
			inventoryPolicy: inventoryPolicy || "deny",
			customSellingPlanId: customSellingPlanId || null,
			usesCustomSellingPlan: usesCustomSellingPlan === true,
		});
	}

	/**
	 * Handle variant select request from product card
	 * @param {CustomEvent} event
	 * @returns {void}
	 */
	handleVariantSelect(event) {
		const { productData } = event.detail || {};
		if (!productData) return;

		const handle = productData.productHandle || productData.handle || "";
		const details = this.getProductDetails(handle);

		console.log("[BYOB] Variant select - handle:", handle);
		console.log("[BYOB] Variant select - details:", details);
		console.log(
			"[BYOB] Variant select - productConfig.details keys:",
			Object.keys(this.productConfig?.details || {}),
		);

		const drawerData = this.buildVariantDrawerData(productData, details);
		console.log("[BYOB] Variant select - drawerData:", drawerData);

		const drawer = this.refs.variantDrawer;
		if (drawer && typeof drawer.open === "function") {
			drawer.open(drawerData);
		}
	}

	/**
	 * Handle variant selected in drawer
	 * @param {CustomEvent} event
	 * @returns {void}
	 */
	handleVariantSelected(event) {
		if (!event?.detail) return;
		this.handleProductAdded({
			detail: {
				...event.detail,
				usesCustomSellingPlan: true,
			},
		});
	}

	/**
	 * Handle quantity changed event from card
	 * @param {CustomEvent} event - The quantity change event
	 * @returns {void}
	 */
	handleQuantityChanged(event) {
		const { variantId, quantity } = event.detail;

		// Check max items limit for increases
		const currentProduct = this.stateManager.getProduct(variantId);
		const currentQuantity = currentProduct?.quantity || 0;

		if (quantity > currentQuantity) {
			const totalCount = this.stateManager.getTotalCount();
			const newTotal = totalCount - currentQuantity + quantity;

			if (newTotal > this.config.maxItems) {
				if (!this.isMobileViewport()) {
					this.showError(
						`Maximum of ${this.config.maxItems} items allowed in your bundle.`,
					);
				}
				return;
			}
		}

		this.stateManager.updateQuantity(variantId, quantity);

		// Dispatch event for product card sync
		document.dispatchEvent(
			new CustomEvent("byob:quantity:update", {
				detail: { variantId, quantity },
			}),
		);
	}

	/**
	 * Handle product removed event
	 * @param {CustomEvent} event - The remove event
	 * @returns {void}
	 */
	handleProductRemoved(event) {
		const { variantId } = event.detail;
		this.stateManager.removeProduct(variantId);

		// Dispatch event for product card sync
		document.dispatchEvent(
			new CustomEvent("byob:product:removed", {
				detail: { variantId },
			}),
		);
	}

	/**
	 * React to state changes
	 * @returns {void}
	 */
	onStateChange() {
		this.renderSelectedProducts();
		this.updateTotalPrice();
		this.updateButtonPrice();
		this.updateCount();
		this.updateEmptyState();
		this.updateButtonStates();
		this.updateShippingProgress();
		this.updateDiscountProgress();
		this.updateBundleProgress();
		this.updateNextTierCta();
		this.updateFreeGiftsState();
		this.updateProductCardBadges();

		// Update mobile product thumbnails
		this.renderMobileProductThumbnails();
		this.scheduleSidebarProductsFadeUpdate();
	}

	/**
	 * Schedule a sidebar fade update after DOM/layout settles
	 * @returns {void}
	 */
	scheduleSidebarProductsFadeUpdate() {
		window.requestAnimationFrame(() => {
			this.updateSidebarProductsFade();
		});
	}

	/**
	 * Update discount badges and prices on all product cards based on current bundle tier.
	 * Badges show the current tier percentage; prices are recalculated so they
	 * reflect the latest discount (not just the initial one).
	 * @returns {void}
	 */
	updateProductCardBadges() {
		if (!this.discountCalculator) return;

		const mainCount = this.stateManager.getMainProductCount();
		const tier =
			mainCount > 0
				? this.discountCalculator.getDisplayTier(mainCount)
				: null;

		const cards = this.querySelectorAll("byob-product-card");
		cards.forEach((card) => {
			const isMainProduct = card.dataset.isMainProduct !== "false";

			// Update discount badge
			const badge = card.querySelector('[data-ref="discountBadge"]');
			if (badge instanceof HTMLElement) {
				if (isMainProduct && tier !== null) {
					badge.textContent = `-${this.formatDisplayDiscount(tier)}%`;
					badge.hidden = false;
				} else {
					badge.hidden = true;
				}
			}

			// Recalculate displayed price so it tracks the current tier
			const productCard =
				/** @type {{ updatePrices?: () => void }} */ (card);
			if (typeof productCard.updatePrices === "function") {
				productCard.updatePrices();
			}
		});
	}

	/**
	 * Update free gifts locked state
	 * @returns {void}
	 */
	updateFreeGiftsState() {
		const freeGifts = this.refs.freeGifts;
		if (!freeGifts) return;
		const mainCount = this.stateManager.getMainProductCount();
		freeGifts.classList.toggle("is-locked", mainCount < 1);
	}

	/**
	 * Render selected products in sidebar
	 * @returns {void}
	 */
	renderSelectedProducts() {
		const container = this.refs.selectedProducts;
		if (!container) return;

		const template = /** @type {HTMLTemplateElement|null} */ (
			document.getElementById("byob-sidecart-item-template")
		);

		const products = this.stateManager.getProducts();
		const variantSellingPlanLookup =
			this.variantSellingPlanLookup.size > 0
				? this.variantSellingPlanLookup
				: this.buildVariantSellingPlanLookup();

		// Clear existing items
		container.innerHTML = "";

		if (!template) {
			// Fallback: create simple list items
			for (const product of products) {
				const item = document.createElement("div");
				item.className = "byob-sidebar-item";
				item.innerHTML = `
          <img src="${product.image}" alt="${product.title}" class="byob-sidebar-item__image">
          <div class="byob-sidebar-item__info">
            <span class="byob-sidebar-item__title">${product.title}</span>
            <span class="byob-sidebar-item__quantity">Qty: ${product.quantity}</span>
          </div>
          <button type="button" class="byob-sidebar-item__remove" data-action="remove" data-variant-id="${product.variantId}" aria-label="Remove ${product.title}">
            &times;
          </button>
        `;
				container.appendChild(item);
			}
			return;
		}

		for (const product of products) {
			const clone = /** @type {DocumentFragment} */ (
				template.content.cloneNode(true)
			);

			// Set variant ID on the item container
			const item = clone.querySelector(".byob-sidecart-item");
			if (item) {
				item.dataset.variantId = product.variantId;
			}

			// Populate template
			const image = /** @type {HTMLImageElement|null} */ (
				clone.querySelector('[data-ref="image"]')
			);
			if (image) {
				image.src = product.image;
				image.alt = product.title;
			}

			const title = clone.querySelector('[data-ref="title"]');
			if (title) {
				title.textContent = product.title;
			}

			const variantName = clone.querySelector(
				'[data-ref="variantName"]',
			);
			if (variantName) {
				const resolvedCustomSellingPlanId =
					product.customSellingPlanId ||
					variantSellingPlanLookup.get(
						String(product.variantId),
					) ||
					null;
				const variantLabel =
					product.variantTitle &&
					product.variantTitle !== "Default Title"
						? product.variantTitle
						: "";

				if (variantLabel || resolvedCustomSellingPlanId) {
					variantName.textContent = resolvedCustomSellingPlanId
						? `${variantLabel || "Variant"}`
						: variantLabel;
					variantName.hidden = false;
				} else {
					variantName.hidden = true;
				}
			}

			// Quantity value (span, not input)
			const quantityValue = clone.querySelector(
				'[data-ref="quantityValue"]',
			);
			if (quantityValue) {
				quantityValue.textContent = String(product.quantity);
			}

			// Calculate discounted price for display
			const originalPrice = parseInt(product.price, 10);
			const compareAtPrice = product.compareAtPrice
				? parseInt(product.compareAtPrice, 10)
				: null;
			const mainQuantity = this.stateManager.getMainProductCount();
			const isMainProduct = product.isMainProduct !== false;

			let discountedLineTotal = originalPrice * product.quantity;
			let formattedLinePrice = this.formatPrice(discountedLineTotal);
			if (this.discountCalculator && isMainProduct) {
				const lineBreakdown =
					this.discountCalculator.getBundleDisplayBreakdown(
						originalPrice * product.quantity,
						mainQuantity,
						this.productConfig?.currencyCode || "USD",
					);
				discountedLineTotal = lineBreakdown.discountedCents;
				formattedLinePrice = lineBreakdown.discounted;
			}

			const price = clone.querySelector('[data-ref="price"]');
			if (price) {
				price.textContent = formattedLinePrice;
			}

			// Show compare-at price only when greater than displayed line price.
			const comparePrice = clone.querySelector(
				'[data-ref="comparePrice"]',
			);
			if (comparePrice) {
				const referenceTotal =
					(compareAtPrice || originalPrice) * product.quantity;
				if (referenceTotal > discountedLineTotal) {
					comparePrice.textContent =
						this.formatPrice(referenceTotal);
					comparePrice.style.display = "";
				} else {
					comparePrice.textContent = "";
					comparePrice.style.display = "none";
				}
			}

			// Show discount badge for the active main-product bundle tier
			const badge = clone.querySelector('[data-ref="badge"]');
			if (badge && this.discountCalculator) {
				const tier = isMainProduct
					? this.discountCalculator.getDisplayTier(mainQuantity)
					: null;
				if (tier !== null) {
					badge.textContent = `-${tier}%`;
					badge.hidden = false;
				} else {
					badge.hidden = true;
				}
			}

			// Set variant ID on all action buttons
			const decreaseBtn = clone.querySelector(
				'[data-ref="decreaseBtn"]',
			);
			if (decreaseBtn) {
				decreaseBtn.dataset.variantId = product.variantId;
			}

			const increaseBtn = clone.querySelector(
				'[data-ref="increaseBtn"]',
			);
			if (increaseBtn) {
				increaseBtn.dataset.variantId = product.variantId;
			}

			const removeBtn = clone.querySelector('[data-ref="removeBtn"]');
			if (removeBtn) {
				removeBtn.dataset.variantId = product.variantId;
				removeBtn.dataset.action = "remove";
			}

			container.appendChild(clone);
		}

		this.scheduleSidebarProductsFadeUpdate();
	}

	/**
	 * Toggle the bottom fade hint for overflowing sidebar items
	 * @returns {void}
	 */
	updateSidebarProductsFade() {
		const container = this.refs.selectedProducts;
		if (!(container instanceof HTMLElement)) return;

		const productCount = this.stateManager.getProducts().length;
		const hasEnoughProducts = productCount >= 3;
		const hasOverflow = container.scrollHeight - container.clientHeight > 4;
		const hasMoreBelow =
			container.scrollTop + container.clientHeight <
			container.scrollHeight - 4;

		container.classList.toggle(
			"has-bottom-fade",
			hasEnoughProducts && hasOverflow && hasMoreBelow,
		);
	}

	/**
	 * Update total price display
	 * @returns {void}
	 */
	updateTotalPrice() {
		const totalPriceEl = this.refs.totalPrice;
		const savingsEl = this.refs.savings;
		const mobileTotalEl = this.refs.mobileTotalPrice;

		if (!this.discountCalculator) return;

		const { originalTotal, finalTotal } = this.getBundleTotals();

		const totalSavings = Math.max(0, originalTotal - finalTotal);
		const formattedTotal = this.formatPrice(Math.round(finalTotal));

		// Update desktop total
		if (totalPriceEl) {
			totalPriceEl.textContent = formattedTotal;
		}

		// Update mobile total
		if (mobileTotalEl) {
			mobileTotalEl.textContent = formattedTotal;
		}

		if (savingsEl) {
			if (totalSavings > 0) {
				savingsEl.textContent = `You save ${this.formatPrice(
					Math.round(totalSavings),
				)}`;
				savingsEl.hidden = false;
			} else {
				savingsEl.hidden = true;
			}
		}
	}

	/**
	 * Update button pricing with compare + final totals
	 * @returns {void}
	 */
	updateButtonPrice() {
		const compareEl = this.refs.buttonComparePrice;
		const finalEl = this.refs.buttonFinalPrice;
		const mobileCompareEl = this.refs.mobileButtonComparePrice;
		const mobileFinalEl = this.refs.mobileButtonFinalPrice;

		if (!this.discountCalculator) return;

		const products = this.stateManager.getProducts();
		const { finalTotal } = this.getBundleTotals();
		let compareTotal = 0;

		for (const product of products) {
			const price = parseInt(product.price, 10) || 0;
			const compareAt = product.compareAtPrice
				? parseInt(product.compareAtPrice, 10) || 0
				: 0;
			const quantity = product.quantity;

			// Compare total should reflect the "was" price when available.
			const compareUnit = compareAt > 0 ? compareAt : price;
			compareTotal += compareUnit * quantity;
		}

		const normalizedFinalTotal = Math.max(finalTotal, 0);
		const formattedFinal = this.formatPrice(normalizedFinalTotal);
		const showFinalPrice = normalizedFinalTotal > 0;

		if (finalEl) {
			finalEl.textContent = formattedFinal;
			finalEl.hidden = !showFinalPrice;
		}

		if (mobileFinalEl) {
			mobileFinalEl.textContent = formattedFinal;
			mobileFinalEl.hidden = !showFinalPrice;
		}

		if (compareTotal > finalTotal) {
			const formattedCompare = this.formatPrice(compareTotal);
			if (compareEl) {
				compareEl.textContent = formattedCompare;
				compareEl.hidden = false;
			}
			if (mobileCompareEl) {
				mobileCompareEl.textContent = formattedCompare;
				mobileCompareEl.hidden = false;
			}
		} else {
			if (compareEl) compareEl.hidden = true;
			if (mobileCompareEl) mobileCompareEl.hidden = true;
		}
	}

	/**
	 * Update bundle progress indicator (1/2/4 items)
	 * @returns {void}
	 */
	updateBundleProgress() {
		const totalQuantity = this.stateManager.getMainProductCount();

		const updateProgress = (container, fill) => {
			if (!container || !fill) return;
			const steps = Array.from(
				container.querySelectorAll(".byob-gamification__step"),
			);

			steps.forEach((step) => {
				const stepCount = parseInt(step.dataset.step || "0", 10);
				if (totalQuantity >= stepCount && stepCount > 0) {
					step.classList.add("is-active");
				} else {
					step.classList.remove("is-active");
				}

				if (this.discountCalculator && stepCount > 0) {
					const bundleDiscount =
						this.discountCalculator.getDiscountForQuantity(
							stepCount,
						);
					const badge = step.querySelector(
						".byob-gamification__step-badge",
					);
					if (badge) {
						badge.textContent = `${this.formatDisplayDiscount(bundleDiscount)}%`;
					}
				}
			});

			const thresholds = steps
				.map((step) => parseInt(step.dataset.step || "0", 10))
				.filter((value) => Number.isFinite(value) && value > 0)
				.sort((a, b) => a - b);
			const progress = this.calculateGamificationProgress(
				totalQuantity,
				thresholds,
			);

			fill.style.width = `${Math.round(progress * 100)}%`;
			container.classList.toggle("has-progress", totalQuantity > 0);
		};

		updateProgress(
			this.refs.bundleProgress,
			this.refs.bundleProgressFill,
		);
		updateProgress(
			this.refs.mobileBundleProgress,
			this.refs.mobileBundleProgressFill,
		);
	}

	/**
	 * Update next tier CTA copy and visibility
	 * @returns {void}
	 */
	updateNextTierCta() {
		const cta = this.refs.nextTierCta;
		const textEl = this.refs.nextTierText;
		if (!cta || !textEl || !this.discountCalculator) return;

		const count = this.stateManager.getMainProductCount();
		const nextTier = this.discountCalculator.getNextTier(count);

		if (!nextTier) {
			cta.hidden = true;
			return;
		}

		const remaining = nextTier.remaining || 0;
		const itemLabel = remaining === 1 ? "item" : "items";
		textEl.textContent = `Add ${remaining} ${itemLabel} to get ${this.formatDisplayDiscount(nextTier.discount)}% off`;
		cta.hidden = false;
	}

	/**
	 * Calculate progress for gamification tiers
	 * @param {number} quantity
	 * @param {number[]} thresholds
	 * @returns {number}
	 */
	calculateGamificationProgress(quantity, thresholds) {
		if (!Array.isArray(thresholds) || thresholds.length === 0) return 0;
		if (quantity <= 0) return 0;
		const max = thresholds[thresholds.length - 1];
		if (quantity >= max) return 1;

		const segment = 1 / thresholds.length;
		let previous = 0;

		for (let index = 0; index < thresholds.length; index += 1) {
			const threshold = thresholds[index];
			if (quantity <= threshold) {
				const span = Math.max(1, threshold - previous);
				const ratio = (quantity - previous) / span;
				return (
					segment * index +
					segment * Math.max(0, Math.min(1, ratio))
				);
			}
			previous = threshold;
		}

		return 1;
	}

	/**
	 * Update item count display
	 * @returns {void}
	 */
	updateCount() {
		const count = this.stateManager.getTotalCount();
		const countText = count === 1 ? "1" : `${count}`;

		// Update desktop count
		const countEl = this.refs.itemCount;
		if (countEl) {
			countEl.textContent = countText;
			countEl.setAttribute("aria-label", `${count} items in bundle`);
		}

		// Update mobile count
		const mobileCountEl = this.refs.mobileItemCount;
		if (mobileCountEl) {
			mobileCountEl.textContent = countText;
		}
	}

	/**
	 * Toggle empty state visibility
	 * @returns {void}
	 */
	updateEmptyState() {
		const emptyState = this.refs.emptyState;
		const selectedProducts = this.refs.selectedProducts;

		if (!emptyState) return;

		const hasProducts = this.stateManager.getTotalCount() > 0;

		emptyState.hidden = hasProducts;

		if (selectedProducts) {
			selectedProducts.hidden = !hasProducts;
		}
	}

	/**
	 * Update button enabled/disabled states
	 * @returns {void}
	 */
	updateButtonStates() {
		const submitBtn = /** @type {HTMLButtonElement|null} */ (
			this.refs.submitBtn
		);
		const mobileSubmitBtn = /** @type {HTMLButtonElement|null} */ (
			this.refs.mobileSubmitBtn
		);

		const count = this.stateManager.getTotalCount();
		const mainCount = this.stateManager.getMainProductCount();
		const isValid =
			mainCount >= this.config.minItems &&
			count <= this.config.maxItems;

		// Update desktop button
		if (submitBtn) {
			submitBtn.disabled = !isValid;

			if (mainCount < this.config.minItems) {
				const remaining = this.config.minItems - mainCount;
				submitBtn.setAttribute(
					"aria-label",
					`Add ${remaining} more main products to continue`,
				);
			} else {
				submitBtn.setAttribute("aria-label", "Add bundle to cart");
			}
		}

		// Update mobile button
		if (mobileSubmitBtn) {
			mobileSubmitBtn.disabled = !isValid;
		}
	}

	/**
	 * Update shipping progress bar
	 * @returns {void}
	 */
	updateShippingProgress() {
		const progressContainers = [
			this.refs.shippingProgress,
			this.refs.mobileShippingProgress,
		].filter(Boolean);
		if (!progressContainers.length || !this.config.freeShippingThreshold)
			return;

		const { finalTotal } = this.getBundleTotals();
		const totalPrice = Math.max(0, Math.round(finalTotal));
		const threshold = this.config.freeShippingThreshold;
		const progress = Math.min((totalPrice / threshold) * 100, 100);
		const isComplete = totalPrice >= threshold;

		for (const container of progressContainers) {
			if (!(container instanceof HTMLElement)) continue;
			const progressEl = container;
			// Update fill element
			const fillEl = progressEl.querySelector(
				'[data-ref="shippingFill"]',
			);
			if (fillEl instanceof HTMLElement) {
				fillEl.style.width = `${progress}%`;
				fillEl.classList.toggle(
					"byob-shipping-progress__fill--complete",
					isComplete,
				);
			}

			// Update text
			const progressText = progressEl.querySelector(
				'[data-ref="shippingText"]',
			);
			const shippingRemaining = progressEl.querySelector(
				'[data-ref="shippingRemaining"]',
			);

			if (progressText) {
				if (isComplete) {
					progressText.innerHTML =
						"Congrats! You've unlocked free shipping.";
					progressText.classList.add(
						"byob-shipping-progress__text--success",
					);
				} else {
					progressText.classList.remove(
						"byob-shipping-progress__text--success",
					);
					const remaining = threshold - totalPrice;
					if (shippingRemaining) {
						shippingRemaining.textContent =
							this.formatPrice(remaining);
					} else {
						progressText.innerHTML = `Add <span data-ref="shippingRemaining">${this.formatPrice(
							remaining,
						)}</span> more for free shipping`;
					}
				}
			}
		}
	}

	/**
	 * Initialize discount progress UI from template
	 * @returns {void}
	 */
	initializeDiscountProgress() {
		const container = this.refs.discountProgress;
		if (!container) return;

		// Check if already initialized
		if (container.querySelector(".byob-discount-progress")) return;

		const template = /** @type {HTMLTemplateElement|null} */ (
			document.getElementById("byob-discount-progress-template")
		);

		if (!template) {
			console.warn(
				"[BundleProductList] Discount progress template not found",
			);
			return;
		}

		const clone = template.content.cloneNode(true);
		container.appendChild(clone);
	}

	/**
	 * Update discount tier progress
	 * @returns {void}
	 */
	updateDiscountProgress() {
		const progressEl = this.refs.discountProgress;
		if (!progressEl || !this.discountCalculator) return;

		// Ensure template is rendered
		this.initializeDiscountProgress();

		const count = this.stateManager.getMainProductCount();
		const currentDiscount =
			this.discountCalculator.getDiscountForQuantity(count);
		const nextTier = this.discountCalculator.getNextTier(count);

		const currentDiscountEl = progressEl.querySelector(
			'[data-ref="discountValue"]',
		);
		if (currentDiscountEl) {
			currentDiscountEl.textContent =
				currentDiscount > 0
					? `${this.formatDisplayDiscount(currentDiscount)}%`
					: "0%";
		}

		const nextTierEl = progressEl.querySelector(
			'[data-ref="nextTierInfo"]',
		);
		if (nextTierEl) {
			if (nextTier) {
				const itemsNeeded = nextTierEl.querySelector(
					'[data-ref="itemsNeeded"]',
				);
				const nextDiscount = nextTierEl.querySelector(
					'[data-ref="nextDiscount"]',
				);
				if (itemsNeeded)
					itemsNeeded.textContent = String(nextTier.remaining);
				if (nextDiscount)
					nextDiscount.textContent = `${this.formatDisplayDiscount(nextTier.discount)}%`;
				nextTierEl.hidden = false;
			} else {
				nextTierEl.hidden = true;
			}
		}

		// Update savings display
		const savingsInfo = progressEl.querySelector(
			'[data-ref="savingsInfo"]',
		);
		const savingsAmount = progressEl.querySelector(
			'[data-ref="savingsAmount"]',
		);
		if (savingsInfo && savingsAmount) {
			const { originalTotal, finalTotal } = this.getBundleTotals();
			const totalSavings = Math.max(0, originalTotal - finalTotal);
			if (totalSavings > 0) {
				savingsAmount.textContent = this.formatPrice(
					Math.round(totalSavings),
				);
				savingsInfo.hidden = false;
			} else {
				savingsInfo.hidden = true;
			}
		}
	}

	/**
	 * Calculate bundle totals by discounting the combined main-product subtotal once.
	 * This avoids per-line rounding errors on multi-line bundles.
	 * @returns {{originalTotal: number, finalTotal: number}}
	 */
	getBundleTotals() {
		if (!this.discountCalculator) {
			return { originalTotal: 0, finalTotal: 0 };
		}

		const products = this.stateManager.getProducts();
		const mainQuantity = this.stateManager.getMainProductCount();
		let originalTotal = 0;
		let mainSubtotal = 0;
		let nonMainSubtotal = 0;

		for (const product of products) {
			const unitPrice = parseInt(product.price, 10) || 0;
			const lineOriginal = unitPrice * product.quantity;
			originalTotal += lineOriginal;

			if (product.isMainProduct === false) {
				nonMainSubtotal += lineOriginal;
			} else {
				mainSubtotal += lineOriginal;
			}
		}

		const discountedMainSubtotal =
			mainSubtotal > 0
				? this.discountCalculator.getBundleDisplayBreakdown(
						mainSubtotal,
						mainQuantity,
						this.productConfig?.currencyCode || "USD",
					).discountedCents
				: 0;

		return {
			originalTotal,
			finalTotal: discountedMainSubtotal + nonMainSubtotal,
		};
	}

	/**
	 * Render product thumbnails in mobile bar
	 * @returns {void}
	 */
	renderMobileProductThumbnails() {
		const container = this.refs.mobileProducts;
		const emptyEl = this.refs.mobileEmpty;
		if (!container) return;

		const products = this.stateManager.getProducts();

		// Clear existing thumbnails/cards (but keep empty element)
		const thumbs = container.querySelectorAll(
			".byob-mobile-bar__product-thumb, .byob-mobile-bar__add-card",
		);
		thumbs.forEach((thumb) => thumb.remove());

		// Show/hide empty state
		if (emptyEl) {
			emptyEl.hidden = products.length > 0;
		}

		const mainCount = this.stateManager.getMainProductCount();
		const canAddMore =
			this.stateManager.getTotalCount() < this.config.maxItems;
		const nextTier =
			this.discountCalculator?.getNextTier(mainCount) || null;

		// Add incentive card at the beginning of the row
		if (canAddMore) {
			const addCard = document.createElement("button");
			addCard.type = "button";
			addCard.className = "byob-mobile-bar__add-card";
			addCard.setAttribute(
				"aria-label",
				nextTier
					? `Add ${nextTier.remaining} more to get ${this.formatDisplayDiscount(nextTier.discount)}% off`
					: "Add another product",
			);
			addCard.title = nextTier
				? `Add ${nextTier.remaining} more to get ${this.formatDisplayDiscount(nextTier.discount)}% off`
				: "Add another product";
			addCard.innerHTML = `<span class="byob-mobile-bar__add-card-plus" aria-hidden="true">+</span>`;
			container.appendChild(addCard);
		}

		// Create thumbnails for each product
		for (const product of products) {
			const thumb = document.createElement("button");
			thumb.type = "button";
			thumb.className = "byob-mobile-bar__product-thumb";
			thumb.dataset.variantId = product.variantId;
			thumb.setAttribute("aria-label", `Edit ${product.title}`);

			thumb.innerHTML = `
      <img src="${product.image}" alt="${product.title}">
      ${
			product.quantity > 1
				? `<span class="byob-mobile-bar__product-thumb-badge">${product.quantity}</span>`
				: ""
		}
    `;

			container.appendChild(thumb);
		}

		// Append free gift thumbnails after selected products
		if (mainCount >= 1 && this.refs.freeGifts) {
			const giftTemplate = /** @type {HTMLTemplateElement|null} */ (
				document.getElementById("byob-mobile-gift-template")
			);
			if (!giftTemplate) return;

			const giftElements =
				this.refs.freeGifts.querySelectorAll("[data-free-gift]");
			giftElements.forEach((giftEl) => {
				const srcImg = giftEl.querySelector("img");
				if (!srcImg) return;

				const clone = /** @type {DocumentFragment} */ (
					giftTemplate.content.cloneNode(true)
				);
				const img = clone.querySelector('[data-ref="image"]');
				if (img instanceof HTMLImageElement) {
					img.src = srcImg.src;
					img.alt = srcImg.alt;
				}

				container.appendChild(clone);
			});
		}
	}

	/**
	 * Open product modal for a specific variant
	 * @param {string} variantId - The variant ID to show
	 * @returns {void}
	 */
	openProductModal(variantId) {
		const modal = this.refs.productModal;
		if (!modal) return;

		const normalizedVariantId = String(variantId);
		const product = this.stateManager.getProduct(normalizedVariantId);
		if (!product) return;

		// Store current variant ID
		modal.dataset.variantId = normalizedVariantId;

		// Populate modal
		const image = this.refs.modalImage;
		if (image) {
			image.src = product.image;
			image.alt = product.title;
		}

		const title = this.refs.modalTitle;
		if (title) title.textContent = product.title;

		const variant = this.refs.modalVariant;
		if (variant) {
			if (
				product.variantTitle &&
				product.variantTitle !== "Default Title"
			) {
				variant.textContent = product.variantTitle;
				variant.hidden = false;
			} else {
				variant.hidden = true;
			}
		}

		const price = this.refs.modalPrice;
		if (price) {
			this.updateModalPrice(product, product.quantity);
		}

		const quantity = this.refs.modalQuantity;
		if (quantity) quantity.textContent = String(product.quantity);

		// Update button states
		this.updateModalButtonStates(product.quantity);

		// Show modal
		modal.classList.add("byob-product-modal--open");
		document.body.style.overflow = "hidden";
	}

	/**
	 * Close product modal
	 * @returns {void}
	 */
	closeProductModal() {
		const modal = this.refs.productModal;
		if (!modal) return;

		modal.classList.remove("byob-product-modal--open");
		modal.dataset.variantId = "";
		document.body.style.overflow = "";
	}

	/**
	 * Update modal quantity button states
	 * @param {number} quantity - Current quantity
	 * @returns {void}
	 */
	updateModalButtonStates(quantity) {
		const decreaseBtn = this.refs.modalDecrease;
		const increaseBtn = this.refs.modalIncrease;

		if (decreaseBtn) {
			decreaseBtn.disabled = quantity <= 1;
		}

		if (increaseBtn) {
			const totalCount = this.stateManager.getTotalCount();
			const currentProduct = this.stateManager.getProduct(
				this.refs.productModal?.dataset.variantId || "",
			);
			const currentQty = currentProduct?.quantity || 0;
			const inventoryLimit = currentProduct?.inventory ?? 9999;
			const canContinueSelling =
				String(
					currentProduct?.inventoryPolicy || "deny",
				).toLowerCase() === "continue";
			const otherItems = totalCount - currentQty;
			// Disable if max items reached OR inventory limit reached
			increaseBtn.disabled =
				otherItems + quantity >= this.config.maxItems ||
				(!canContinueSelling && quantity >= inventoryLimit);
		}
	}

	/**
	 * Decrease quantity in modal
	 * @returns {void}
	 */
	decreaseModalQuantity() {
		const modal = this.refs.productModal;
		const variantId = modal?.dataset.variantId;
		if (!variantId) return;

		const product = this.stateManager.getProduct(variantId);
		if (!product || product.quantity <= 1) return;

		const newQuantity = product.quantity - 1;
		this.stateManager.updateQuantity(variantId, newQuantity);

		// Dispatch event for product card sync
		document.dispatchEvent(
			new CustomEvent("byob:quantity:update", {
				detail: { variantId, quantity: newQuantity },
			}),
		);

		// Update modal display
		const quantityEl = this.refs.modalQuantity;
		if (quantityEl) quantityEl.textContent = String(newQuantity);

		const price = this.refs.modalPrice;
		if (price) {
			this.updateModalPrice(product, newQuantity);
		}

		this.updateModalButtonStates(newQuantity);
	}

	/**
	 * Increase quantity in modal
	 * @returns {void}
	 */
	increaseModalQuantity() {
		const modal = this.refs.productModal;
		const variantId = modal?.dataset.variantId;
		if (!variantId) return;

		const product = this.stateManager.getProduct(variantId);
		if (!product) return;

		// Check max limit and inventory limit
		const totalCount = this.stateManager.getTotalCount();
		const inventoryLimit = product.inventory ?? 9999;
		const canContinueSelling =
			String(product.inventoryPolicy || "deny").toLowerCase() ===
			"continue";
		if (totalCount >= this.config.maxItems) return;
		if (!canContinueSelling && product.quantity >= inventoryLimit) return;

		const newQuantity = product.quantity + 1;
		this.stateManager.updateQuantity(variantId, newQuantity);

		// Dispatch event for product card sync
		document.dispatchEvent(
			new CustomEvent("byob:quantity:update", {
				detail: { variantId, quantity: newQuantity },
			}),
		);

		// Update modal display
		const quantityEl = this.refs.modalQuantity;
		if (quantityEl) quantityEl.textContent = String(newQuantity);

		const price = this.refs.modalPrice;
		if (price) {
			this.updateModalPrice(product, newQuantity);
		}

		this.updateModalButtonStates(newQuantity);
	}

	/**
	 * Remove product from modal
	 * @returns {void}
	 */
	removeModalProduct() {
		const modal = this.refs.productModal;
		const variantId = modal?.dataset.variantId;
		if (!variantId) return;

		this.stateManager.removeProduct(variantId);

		// Dispatch event for product card sync
		document.dispatchEvent(
			new CustomEvent("byob:product:removed", {
				detail: { variantId },
			}),
		);

		// Close modal
		this.closeProductModal();
	}

	/**
	 * Submit bundle to cart
	 * @returns {Promise<void>}
	 */
	async addToCart() {
		const count = this.stateManager.getTotalCount();
		const mainCount = this.stateManager.getMainProductCount();

		// Validate minimum main products
		if (mainCount < this.config.minItems) {
			this.showError(
				`Please add at least ${this.config.minItems} main products to your bundle.`,
			);
			return;
		}

		if (!this.cartSubmitter) {
			this.showError(
				"Cart submitter not initialized. Please refresh the page.",
			);
			return;
		}

		this.setLoading(true);

		try {
			const products = this.stateManager.getProducts().map((p) => {
				return {
					id: p.variantId,
					quantity: p.quantity,
					isMainProduct: p.isMainProduct !== false,
					customSellingPlanId:
						p.isMainProduct !== false
							? this.config.mainSellingPlanId || null
							: null,
					usesCustomSellingPlan: false,
				};
			});
			const freeGifts = this.getFreeGiftVariantIds(count >= 1);
			const result = await this.cartSubmitter.submit(
				products,
				freeGifts,
			);

			console.log({ result });
			if (!result.success) {
				this.showError(
					result.error || "Failed to add bundle to cart.",
				);
			}
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "An unexpected error occurred.";
			this.showError(message);
		} finally {
			this.setLoading(false);
		}
	}

	/**
	 * Collect free gift variant IDs from sidebar
	 * @param {boolean} unlocked - Whether gifts should be included
	 * @returns {number[]}
	 */
	getFreeGiftVariantIds(unlocked) {
		if (!unlocked) return [];
		const freeGifts = this.refs.freeGifts;
		if (!freeGifts) return [];
		return Array.from(freeGifts.querySelectorAll("[data-free-gift]"))
			.map((el) =>
				parseInt(el.getAttribute("data-variant-id") || "0", 10),
			)
			.filter((id) => id > 0);
	}

	/**
	 * Handle successful cart submission
	 * @returns {void}
	 */
	handleCartSuccess() {
		// Clear state
		this.stateManager.clear();

		// Reset all product cards to quantity 0
		this.resetAllProductCards();

		// Force UI recalculation/reset (guards against racey re-renders)
		this.onStateChange();

		// Show confirmation
		this.showConfirmation("Bundle added to cart!");

		// Dispatch event for theme integration
		this.dispatchEvent(
			new CustomEvent("byob:bundle:added", {
				bubbles: true,
				detail: { boxId: this.config.boxId },
			}),
		);
	}

	/**
	 * Reset all product cards to their initial state (quantity 0)
	 * @returns {void}
	 */
	resetAllProductCards() {
		const productCards = this.querySelectorAll("byob-product-card");
		productCards.forEach((card) => {
			// Dispatch remove event for each card's variant
			// @ts-ignore
			const variantId =
				card.selectedVariant?.platformId ||
				card.selectedVariant?.id;
			if (variantId) {
				document.dispatchEvent(
					new CustomEvent("byob:product:removed", {
						detail: { variantId },
					}),
				);
			}
		});
	}

	/**
	 * Toggle loading state
	 * @param {boolean} loading - Whether loading is active
	 * @returns {void}
	 */
	setLoading(loading) {
		const submitBtn = /** @type {HTMLButtonElement|null} */ (
			this.refs.submitBtn
		);
		const mobileSubmitBtn = /** @type {HTMLButtonElement|null} */ (
			this.refs.mobileSubmitBtn
		);

		// Update desktop button
		if (submitBtn) {
			submitBtn.disabled = loading;

			const textEl = submitBtn.querySelector(
				".byob-sidebar__submit-text",
			);
			const spinnerEl = submitBtn.querySelector(
				".byob-sidebar__submit-spinner",
			);

			if (loading) {
				if (textEl) textEl.textContent = "Adding...";
				if (spinnerEl) spinnerEl.hidden = false;
				submitBtn.setAttribute("aria-busy", "true");
			} else {
				if (textEl) textEl.textContent = "Add to Cart";
				if (spinnerEl) spinnerEl.hidden = true;
				submitBtn.removeAttribute("aria-busy");
			}
		}

		// Update mobile button
		if (mobileSubmitBtn) {
			mobileSubmitBtn.disabled = loading;

			const textEl = mobileSubmitBtn.querySelector(
				".byob-mobile-bar__btn-text",
			);
			const spinnerEl = mobileSubmitBtn.querySelector(
				".byob-mobile-bar__btn-spinner",
			);

			if (loading) {
				if (textEl) textEl.textContent = "Adding...";
				if (spinnerEl) spinnerEl.hidden = false;
				mobileSubmitBtn.setAttribute("aria-busy", "true");
			} else {
				if (textEl) textEl.textContent = "Add to Cart";
				if (spinnerEl) spinnerEl.hidden = true;
				mobileSubmitBtn.removeAttribute("aria-busy");
			}
		}

		this.classList.toggle("is-loading", loading);
	}

	/**
	 * Mark initial data/render load complete.
	 * Keeps submit-button loading behavior separate from first-load masking.
	 * @returns {void}
	 */
	markInitialLoadComplete() {
		this.classList.remove("is-loading");
	}

	/**
	 * Render modal line price with current discount and compare-at strike-through.
	 * @param {{price: string, compareAtPrice?: string|null, isMainProduct?: boolean}} product
	 * @param {number} quantity
	 * @returns {void}
	 */
	updateModalPrice(product, quantity) {
		const priceEl = this.refs.modalPrice;
		if (!priceEl) return;

		const unitPrice = parseInt(product.price, 10) || 0;
		const unitCompare = product.compareAtPrice
			? parseInt(product.compareAtPrice, 10) || 0
			: 0;
		const mainQuantity = this.stateManager.getMainProductCount();
		const isMainProduct = product.isMainProduct !== false;
		const baseTotal = unitPrice * quantity;
		const finalTotal =
			this.discountCalculator && isMainProduct
				? this.discountCalculator.getBundleDisplayBreakdown(
						baseTotal,
						mainQuantity,
						this.productConfig?.currencyCode || "USD",
					).discountedCents
				: baseTotal;
		const compareTotal =
			(unitCompare > 0 ? unitCompare : unitPrice) * quantity;
		const hasBundleDiscount = isMainProduct && finalTotal < baseTotal;

		if (hasBundleDiscount && compareTotal > finalTotal) {
			priceEl.innerHTML = `${this.formatPrice(finalTotal)} <s>${this.formatPrice(compareTotal)}</s>`;
		} else {
			priceEl.textContent = this.formatPrice(finalTotal);
		}
	}

	/**
	 * Display error message
	 * @param {string} message - Error message to display
	 * @returns {void}
	 */
	showError(message) {
		console.error("[BundleProductList]", message);

		// Dispatch event for external error handling
		this.dispatchEvent(
			new CustomEvent("byob:error", {
				bubbles: true,
				detail: { message },
			}),
		);

		// Show inline error if error container exists
		const errorContainer = this.querySelector(
			'[data-ref="errorMessage"]',
		);
		if (errorContainer) {
			errorContainer.textContent = message;
			errorContainer.hidden = false;

			// Auto-hide after 5 seconds
			setTimeout(() => {
				errorContainer.hidden = true;
			}, 5000);
		} else {
			// Fallback to alert for critical errors
			// eslint-disable-next-line no-alert
			alert(message);
		}
	}

	/**
	 * Display confirmation message
	 * @param {string} message - Confirmation message to display
	 * @returns {void}
	 */
	showConfirmation(message) {
		// Dispatch event for external handling
		this.dispatchEvent(
			new CustomEvent("byob:success", {
				bubbles: true,
				detail: { message },
			}),
		);

		// Show inline confirmation if container exists
		const confirmContainer = this.querySelector(
			'[data-ref="confirmMessage"]',
		);
		if (confirmContainer) {
			confirmContainer.textContent = message;
			confirmContainer.hidden = false;

			// Auto-hide after 3 seconds
			setTimeout(() => {
				confirmContainer.hidden = true;
			}, 3000);
		}
	}

	/**
	 * Format price in cents to currency string
	 * @param {number} cents - Price in cents
	 * @returns {string} Formatted price string
	 */
	formatPrice(cents) {
		const amount = (typeof cents === "number" ? cents : 0) / 100;
		const currencyCode = this.productConfig?.currencyCode || "USD";
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}
}

// Register the custom element
customElements.define("bundle-product-list", BundleProductList);

export default BundleProductList;
